import React, { useEffect, useState } from "react";
import { GoChecklist } from "react-icons/go";
import DataTable from "./DataTable";
import StatusPill from "./StatusPill";
import { contexto, etapasFechamento, kpisPainel, empresasPainel, SITUACAO_LABEL } from "../mockData";
import validacaoFiscalService from "../../../services/validacaoFiscalService";

const SITUACAO_VARIANT = { ok: "success", atencao: "warning", critico: "danger" };

const COLUMNS = [
  { key: "empresa", label: "Empresa" },
  { key: "cnpjRaiz", label: "CNPJ raiz" },
  { key: "conciliacao", label: "Conciliação" },
  { key: "divergencias", label: "Divergências" },
  { key: "prazo", label: "Prazo" },
  { key: "responsavel", label: "Responsável" },
  {
    key: "situacao",
    label: "Situação",
    render: (row) => (
      <StatusPill variant={SITUACAO_VARIANT[row.situacao]}>{SITUACAO_LABEL[row.situacao]}</StatusPill>
    ),
  },
];

function formatCurrency(num) {
  if (num === null || num === undefined) return "\u2014";
  return `R$ ${Number(num).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
}

export default function PainelTab({ competenciaId }) {
  const [real, setReal] = useState(null);
  const [loading, setLoading] = useState(!!competenciaId);

  useEffect(() => {
    if (!competenciaId) {
      setReal(null);
      return;
    }
    let cancelado = false;
    setLoading(true);
    validacaoFiscalService
      .obterPainel(competenciaId)
      .then((resp) => {
        if (!cancelado) setReal(resp?.data?.data || null);
      })
      .catch((err) => console.error("[PainelTab] Erro ao buscar painel:", err))
      .finally(() => !cancelado && setLoading(false));
    return () => {
      cancelado = true;
    };
  }, [competenciaId]);

  const diasRestantes = real ? real.diasRestantes : contexto.diasRestantes;
  const etapas = real
    ? real.etapas.map((e) => ({ ordem: e.ordem, status: e.status, label: e.label }))
    : etapasFechamento;
  const kpis = real
    ? [
        { label: "Documentos conciliados", valor: `${real.indicadores.percentualConciliado}%`, caption: `${real.indicadores.documentosConciliados} de ${real.indicadores.documentosTotal} chaves` },
        { label: "Divergências abertas", valor: String(real.indicadores.divergenciasAbertas), caption: `${real.indicadores.divergenciasCriticas} críticas` },
        { label: "Crédito travado (a apropriar)", valor: formatCurrency(real.indicadores.creditoTravadoTotal), caption: `${real.indicadores.creditoTravadoFornecedores} fornecedores` },
        { label: "Saldo recalculado", valor: formatCurrency(real.indicadores.saldoRecalculado?.saldoAPagar), caption: "a pagar · recalculado pela conciliação" },
      ]
    : kpisPainel;

  return (
    <>
      <div className="countdown-banner">
        <div className="countdown-number">{diasRestantes}</div>
        <div className="countdown-text">
          <strong>dias corridos até o fim do prazo de ajustes.</strong> Sem validação aprovada, o saldo
          da AA é presumido correto (Decreto 12.955/2026, art. 46, § 7º).
        </div>
        <div className="countdown-tag">
          {real ? `Competência ${String(real.competencia.mes).padStart(2, "0")}/${real.competencia.ano}` : "Livro: CBS · IBS entra na fase 3"}
        </div>
      </div>

      <div className="steps-card">
        <div className="steps-title">Etapas do fechamento {loading && "(atualizando…)"}</div>
        <ol className="steps-list">
          {etapas.map((etapa) => (
            <li key={etapa.ordem} className={`step-item ${etapa.status}`}>
              <div className="step-status">
                {etapa.ordem} ·{" "}
                {etapa.status === "concluida" ? "Concluída" : etapa.status === "atual" ? "Em andamento" : "Pendente"}
              </div>
              <div className="step-label">{etapa.label}</div>
            </li>
          ))}
        </ol>
      </div>

      <div className="kpi-cards">
        {kpis.map((kpi) => (
          <div className="kpi-card" key={kpi.label}>
            <div className="kpi-label">{kpi.label}</div>
            <div className="kpi-value">{kpi.valor}</div>
            <div className="kpi-caption">{kpi.caption}</div>
          </div>
        ))}
      </div>

      <div className="section-title">
        <GoChecklist />
        <h4>Situação por CNPJ raiz</h4>
      </div>
      <DataTable
        subtitle="Apuração consolidada por raiz; pagamento e ressarcimento na matriz (arts. 42–43)"
        columns={COLUMNS}
        rows={empresasPainel}
        minWidth={820}
      />

      <div className="table-footnote">
        {real
          ? "Tabela de situação por CNPJ raiz ainda ilustrativa nesta versão (agregação multi-empresa é um próximo passo do backend)."
          : "Dados ilustrativos para validação do desenho. Frequência de processamento da AA e critério cronológico ficam como parâmetros configuráveis."}
      </div>
    </>
  );
}
